import { UserEntityPermissionsType } from '@/models/users/user-permissions/user-entity-permissions';
import * as moment from 'moment';
import { OrderShippingAddress, OrderShippingAddressType } from './order-shipping-address';
import { PaymentMethod } from '@/models/common/payment-method';
import { BusinessOrderSku } from './business-order-sku';
import i18n from '@/plugins/i18n';
import { appService } from '@/services';

export class BusinessOrder {
    public id: number = 0;
    public consumerName?: string;
    public code?: string;
    public createdUtc?: moment.Moment;
    public businessId: number = 0;
    public businessTradeName?: string;
    public buyerEmail?: string;
    public deliveryInfo: OrderShippingAddress[] = [];
    public paymentMethod: PaymentMethod | null = null;
    public totalPrice?: number;
    public shippingPrice?: number;
    public remarks?: string;
    public viewed: boolean = true;
    public skus: BusinessOrderSku[] = [];

    public get permissionsGroupType(): UserEntityPermissionsType {
        return UserEntityPermissionsType.BusinessOrders;
    }

    public get permissionsIdToCheck(): number {
        return this.id;
    }

    public get created(): moment.Moment | undefined {
        return this.createdUtc
            ? this.createdUtc.local()
            : undefined;
    }

    public get applicationName(): string | undefined {
        return this.consumerName
            ? this.consumerName
            : appService.getCurrentApplication().commercialName;
    }

    public get deliveryInfoDetails(): string | undefined {
        if (this.deliveryInfo[0].type! === OrderShippingAddressType.Pickup) {
            return i18n.t('views.orders.businessOrdersListView.deliveryAtBusinessPremises').toString();
        }

        if (this.deliveryInfo[0].type! === OrderShippingAddressType.ReceiveDigitally) {
            return i18n.t('views.orders.businessOrdersListView.receiveDigitally').toString();
        }

        if (this.deliveryInfo[0].type! === OrderShippingAddressType.Delivery) {
            const postalCode: string = this.deliveryInfo[0].address!.postalCode!;
            const city: string | undefined = this.deliveryInfo[0].address ? this.deliveryInfo[0].address!.city : undefined;
            const stateName: string | undefined = this.deliveryInfo[0].address ? this.deliveryInfo[0].address!.stateName : undefined;

            if (city && stateName) {
                return `${postalCode} (${city}, ${stateName})`;
            }

            if (!city && stateName) {
                return `${postalCode} (${stateName})`;
            }

            if (city && !stateName) {
                return `${postalCode} (${city})`;
            }

            return postalCode;
        }

        return undefined;
    }

    public get paymentMethodName(): string | null {
        switch (this.paymentMethod) {
            case PaymentMethod.BankTransfer:
                return i18n.t('models.orders.businessOrder.bankTransfer').toString();
            case PaymentMethod.PaymentAtLocation:
                return i18n.t('models.orders.businessOrder.paymentInPremises').toString();
            case PaymentMethod.PayPal:
                return i18n.t('models.orders.businessOrder.payPal').toString();
            case PaymentMethod.RedsysCreditCard:
                return i18n.t('models.orders.businessOrder.paymentByCard').toString();
            case PaymentMethod.RedsysBizum:
                return i18n.t('models.orders.businessOrder.paymentByBizum').toString();
            case PaymentMethod.PersonalBizum:
                return i18n.t('models.orders.businessOrder.paymentByBizumPersonal').toString();
            default:
                return i18n.t('models.orders.businessOrder.noPaymentRequired').toString();
        }
    }

    constructor(json?: any) {
        if (!json) {
            return;
        }

        Object.assign(
            this,
            json,
            {
                createdUtc: moment.utc(json.createdUtc),
                deliveryInfo: json.deliveryInfo ? json.deliveryInfo.map((address: any) => new OrderShippingAddress(address)) : [],
                paymentMethod: PaymentMethod[json.paymentMethod],
                skus: json.skus ? json.skus.map((sku: any) => new BusinessOrderSku(sku)) : [],
            });
    }
}
